import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";

// ConvexError codes that represent expected user-facing states, not bugs.
// Stale links, deleted resources and expired sessions are normal — they should
// render a friendly UI, not spam Sentry.
const EXPECTED_CONVEX_ERROR_CODES = new Set([
  "NOT_FOUND",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "USER_NOT_FOUND",
  "TRIAL_ALREADY_USED",
  "GENERATION_LIMIT_REACHED",
  "INVALID_PAYLOAD",
  "DUPLICATE",
  "LIMIT_REACHED",
]);

function isExpectedConvexError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const data = (error as { data?: unknown }).data;
  if (data && typeof data === "object" && "code" in data) {
    const code = (data as { code?: unknown }).code;
    if (typeof code === "string" && EXPECTED_CONVEX_ERROR_CODES.has(code)) {
      return true;
    }
  }
  return false;
}

// onnxruntime-web (used by the background-removal worker) routes native
// stderr through console.error, which captureConsoleIntegration then scoops
// up as Sentry errors. The lines are warnings, not failures.
const ONNX_NOISE_PATTERNS = [
  "W:onnxruntime:",
  "session_state.cc:",
  "VerifyEachNodeIsAssignedToAnEp",
  "graph_partitioner.cc:",
];
function isOnnxRuntimeNoise(event: Sentry.ErrorEvent): boolean {
  const message = event.message || "";
  const excValue = event.exception?.values?.[0]?.value || "";
  const haystack = message + "\n" + excValue;
  return ONNX_NOISE_PATTERNS.some((p) => haystack.includes(p));
}

// React reconciling over a DOM tree mutated by an in-app browser translator
// or userscript (`parent.insertBefore` on a node that React thinks is a
// child but the translator has already moved). Can't fix from our side —
// filter to unblock quota.
function isTranslatorDomNoise(event: Sentry.ErrorEvent): boolean {
  const exc = event.exception?.values?.[0];
  if (exc?.type !== "NotFoundError") return false;
  return (exc.value || "").includes("The object can not be found here");
}

// rrweb inside posthog-js fails to unpatch some browser APIs on older/niche
// browsers (e.g. Yandex 25.x) when the session recorder stops on idle,
// throwing "Cannot read properties of undefined (reading 'prototype')".
// It's library noise — filter it out.
function isPosthogRecorderError(event: Sentry.ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values?.length) return false;
  for (const exc of values) {
    const frames = exc.stacktrace?.frames;
    if (!frames) continue;
    for (const frame of frames) {
      const file = frame.filename || frame.abs_path || "";
      if (
        file.includes("posthog-recorder") ||
        file.includes("posthog-js/src/sessionid") ||
        file.includes("posthog-js/src/utils/simple-event-emitter")
      ) {
        return true;
      }
    }
  }
  return false;
}

const SENTRY_ENV =
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ||
  (process.env.NODE_ENV === "production" ? "production" : "development");

// Expected application error codes (thrown via ConvexError<string> from Convex
// actions, caught and localized by the form). Not bugs — skip Sentry.
const EXPECTED_APP_ERROR_CODES = [
  "emailInUseGoogle", "emailInUseApple", "emailInUseTelegram", "emailInUsePassword",
  "invalidEmail", "weakPassword", "invalidCredentials",
  "otpNotFound", "otpExpired", "otpWrong", "otpLocked",
  "resetLinkInvalid", "resetLinkUsed", "resetLinkExpired",
];

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: SENTRY_ENV,

  // Performance: sample 25% — enough to see Safari/minority browsers without burning quota
  tracesSampleRate: 0.25,

  // No session replay
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,

  // Link Sentry errors to PostHog sessions + auto-capture console.error
  integrations: [
    ...(posthog.sentryIntegration ? [posthog.sentryIntegration()] : []),
    Sentry.captureConsoleIntegration({ levels: ["error"] }),
  ],

  beforeSend(event, hint) {
    if (isExpectedConvexError(hint?.originalException)) {
      return null;
    }
    if (isPosthogRecorderError(event)) {
      return null;
    }
    if (isOnnxRuntimeNoise(event)) {
      return null;
    }
    if (isTranslatorDomNoise(event)) {
      return null;
    }
    // captureConsoleIntegration turns ConvexErrors into string messages,
    // so also filter by the serialized error code in the message.
    const message = event.message || "";
    for (const code of EXPECTED_CONVEX_ERROR_CODES) {
      if (message.includes(`"code":"${code}"`)) {
        return null;
      }
    }
    // Transient Convex disconnect — client auto-reconnects and call sites
    // that care (e.g. chat-view validateContent) already fall through.
    if (message.includes("Connection lost while action was in flight")) {
      return null;
    }
    // Next.js RSC prefetch failing (502/network) is handled by the router —
    // it falls back to full browser navigation, so the console.error is noise.
    if (message.includes("Failed to fetch RSC payload")) {
      return null;
    }

    // Expected auth codes — form catches + shows localized UI, Sentry noise only.
    for (const code of EXPECTED_APP_ERROR_CODES) {
      if (message.includes(code)) return null;
    }

    // RSC prefetch aborted when user navigates away — browser cancels the
    // background fetch, surfaces as "Failed to fetch" / "Load failed" against
    // our own domain with ?_rsc= in breadcrumbs. Not a real failure.
    const exc = event.exception?.values?.[0];
    const excValue = exc?.value || "";
    if (
      (excValue === "Failed to fetch" || excValue === "Load failed") &&
      event.breadcrumbs?.some((b) => (b.data?.url as string | undefined)?.includes("_rsc="))
    ) {
      return null;
    }

    return event;
  },
});
